import type { MessageProps } from './types';

import { CheckIcon, DeclineIcon, ThumbUpIcon, Tooltip, classNames } from '@movingimage-evp/mi-ui-component-library';
import { formatDistance } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../../components/avatar';
import { MessageCustomType } from '../../../components/chat/chat.types';
import { LinkifiedMessage } from '../../../components/chat/linkified-message';
import { useChat } from '../../../components/chat/use-sendbird-chat';

import styles from './message.module.css';

export function UserMessage({
  message,
  testId = 'chat-message',
  moderationStatusVisible = false,
  readOnly,
  anonymous,
  locale,
  onApprove,
  onDecline,
  onVote,
}: MessageProps) {
  const { t } = useTranslation();
  const { user, getVotesByMessageId, hasUserVotedForMessage } = useChat();

  const fullTestId = `${testId}-${message.messageId}`;
  const numberOfVotes = getVotesByMessageId(message.messageId);
  const sentByCurrentUser = message.sender.userId === user?.userId;
  const upvotedByCurrentUser = hasUserVotedForMessage(message.messageId);
  const messageUnmoderated = !message.customType;
  const messageApproved = message.customType === MessageCustomType.APPROVED_BY_MODERATOR;
  const messageDeclined = message.customType === MessageCustomType.DECLINED_BY_MODERATOR;
  const approveButtonVisible = messageUnmoderated || messageDeclined;
  const declineButtonVisible = messageUnmoderated || messageApproved;
  const votingEnabled = !readOnly && !sentByCurrentUser;
  const moderationEnabled = !readOnly && !sentByCurrentUser && onApprove && onDecline;
  const nickname = anonymous ? '' : message.sender.nickname;
  const sender = sentByCurrentUser
    ? t('components.chatModerationDashboard.message.me')
    : nickname || t('components.chatModerationDashboard.message.anonymous');

  const getInitials = (value: string) => {
    const [name = '', surname = ''] = value.split(' ');
    return `${name[0] || 'A'}${surname[0] || ''}`;
  };

  return (
    <div
      className={classNames(styles.wrapper, sentByCurrentUser && styles.sentByCurrentUser, readOnly && styles.readOnly)}
      data-testid={fullTestId}
    >
      <header className={styles.header}>
        <div className={styles.sender}>
          <Avatar data-testid={`${fullTestId}-avatar`} className={styles.avatar}>
            {getInitials(nickname)}
          </Avatar>

          <div className={styles.info}>
            <span data-testid={`${fullTestId}-sender`} className={styles.nickname}>
              {sender}
            </span>

            <span data-testid={`${fullTestId}-date`} className={styles.date}>
              {formatDistance(new Date(message.createdAt || 0), new Date(), { addSuffix: true, locale })}
            </span>
          </div>
        </div>

        <Tooltip label={t('components.chatModerationDashboard.message.upvote')} hidden={!votingEnabled}>
          <button
            type="button"
            data-testid={`${fullTestId}-upvote-button`}
            aria-label={t('components.chatModerationDashboard.message.upvote')}
            className={classNames(
              styles.upvotes,
              upvotedByCurrentUser && styles.upvotedByCurrentUser,
              numberOfVotes > 0 && styles.upvoted
            )}
            onClick={votingEnabled ? onVote : undefined}
          >
            <ThumbUpIcon />
            {numberOfVotes > 0 && numberOfVotes}
          </button>
        </Tooltip>
      </header>

      {moderationStatusVisible && messageApproved && (
        <span data-testid={`${fullTestId}-approved-label`} className={classNames(styles.badge, styles.approved)}>
          {t('components.chatModerationDashboard.message.approved')}
        </span>
      )}

      {moderationStatusVisible && messageDeclined && (
        <span data-testid={`${fullTestId}-declined-label`} className={classNames(styles.badge, styles.declined)}>
          {t('components.chatModerationDashboard.message.declined')}
        </span>
      )}

      <div data-testid={`${fullTestId}-text`} className={styles.text}>
        <LinkifiedMessage>{message.message}</LinkifiedMessage>
      </div>

      {moderationEnabled && (
        <footer className={styles.footer}>
          {approveButtonVisible && (
            <button
              type="button"
              data-testid={`${fullTestId}-approve-button`}
              className={classNames(styles.moderationButton, styles.approve)}
              onClick={onApprove}
            >
              <CheckIcon />
              {t('components.chatModerationDashboard.message.approve')}
            </button>
          )}

          {declineButtonVisible && (
            <button
              type="button"
              data-testid={`${fullTestId}-decline-button`}
              className={classNames(styles.moderationButton, styles.decline)}
              onClick={onDecline}
            >
              <DeclineIcon />
              {t('components.chatModerationDashboard.message.decline')}
            </button>
          )}
        </footer>
      )}
    </div>
  );
}
